// client/pages/AdminPapierkorb.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../constants/api.js";
import { smartBack } from "../constants/nav";
import { useConfirm } from "../components/ui/ConfirmationModal.jsx";
import { showSuccess, showError } from "../utils/toast.js";
import { Trash2, Search, AlertTriangle } from "lucide-react";
import AppButton from "../components/ui/AppButton.jsx";
import PillBadge from "../components/ui/PillBadge.jsx";
import OptionPopoverFilter from "../components/ui/OptionPopoverFilter.jsx";
import { Input } from "../components/forms/shared/FormAtoms.jsx";
import {
  detailPageHeaderNavActionsClassName,
  detailPageHeaderNavClassName,
  entityRowCompactActionClassName,
  entityRowCompactDeleteActionClassName,
  mobilePagePaddingClassName,
} from "../design/tokens.js";
import { cn } from "../utils/themeClasses.js";

const ENTITY_OPTIONS = [
  { value: "", label: "Alle Entities" },
  { value: "kunden", label: "Kunden" },
  { value: "objekte", label: "Objekte" },
  { value: "offerten", label: "Offerten" },
  { value: "serviceauftraege", label: "Serviceaufträge" },
  { value: "mangelberichte", label: "Mängelberichte" },
  { value: "pendenzen", label: "Pendenzen" },
  { value: "inventar_artikel", label: "Inventar" },
  { value: "kalender_eintraege", label: "Kalendereinträge" },
  { value: "projekte_elektro", label: "Projekte (Elektro)" },
  { value: "projekte_pv", label: "Projekte (PV)" },
  { value: "projekte_sicherheit", label: "Projekte (Sicherheit)" },
  { value: "projekte_ladestation", label: "Projekte (Ladestation)" },
  { value: "projekte_intern", label: "Projekte (Intern)" },
  { value: "projekte_ladenbau", label: "Projekte (Ladenbau)" },
  { value: "regie_elektro", label: "Regie (Elektro)" },
  { value: "regie_pv", label: "Regie (PV)" },
  { value: "regie_sicherheit", label: "Regie (Sicherheit)" },
  { value: "regie_ladestation", label: "Regie (Ladestation)" },
  { value: "regie_intern", label: "Regie (Intern)" },
  { value: "weiterbildungen", label: "Weiterbildungen" },
  { value: "feiertage", label: "Feiertage" },
  { value: "daily_quotes", label: "Zitate" },
  { value: "service_credentials", label: "Zugangsdaten" },
  { value: "service_contracts", label: "Wartungsverträge" },
];

const ENTITY_FILTER_OPTIONS = ENTITY_OPTIONS.filter(o => o.value).map(o => ({
  value: o.value,
  label: o.label,
}));

const PAGE_SIZE = 50;
const listRowActionsCls =
  "inline-flex flex-nowrap items-center justify-end gap-1.5 shrink-0";

const cardCls = "rounded-2xl border border-default bg-surface";
const thCls =
  "py-2.5 px-3 border-b border-default text-left text-xs uppercase tracking-wide text-secondary font-medium";
const tdCls = "py-2.5 px-3 align-top text-primary";

function formatDate(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateOnly(v) {
  if (!v) return null;
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function clipText(s, max = 72) {
  const t = String(s || "").trim();
  if (!t) return "";
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

/** Anzeigename für Zeile & Bestätigungsdialoge */
function formatEntryTitle(item, users = {}) {
  const name = String(item?.name || "").trim();
  if (name && name !== String(item?.id)) return name;
  if (item?.originalname) return String(item.originalname).trim();
  if (item?.user_name) return `${item.entity_label}: ${item.user_name}`;
  const uid = item?.user_id;
  if (uid && users[uid]) return `${item.entity_label}: ${users[uid]}`;
  if (item?.kunde_name) return `${item.entity_label}: ${item.kunde_name}`;
  if (item?.entity_label) return `${item.entity_label} #${item.id}`;
  return `#${item?.id ?? "?"}`;
}

function daysAgo(iso) {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (diff === 0) return "Heute";
  if (diff === 1) return "Gestern";
  return `Vor ${diff} Tagen`;
}

function formatKundeOrt(item) {
  const plz = String(item.kunde_plz || "").trim();
  const ort = String(item.kunde_ort || item.adresse_ort || item.ort || "").trim();
  if (plz && ort) return `${plz} ${ort}`;
  return plz || ort || "";
}

function buildMetaLine(item, users = {}) {
  const parts = [];
  const kundeLabel = String(item.kunde_name || item.snap_kunde_name || "").trim();
  if (kundeLabel) {
    const ortLine = formatKundeOrt(item);
    parts.push(ortLine ? `Kunde: ${kundeLabel} (${ortLine})` : `Kunde: ${kundeLabel}`);
  }
  if (item.objekt_name) parts.push(`Objekt: ${String(item.objekt_name).trim()}`);
  if (item.projekt_name) {
    const typ = item.projekt_typ || item.projekt_ref_typ;
    const typLbl = typ ? ` (${String(typ).replace(/_/g, " ")})` : "";
    parts.push(`Projekt: ${String(item.projekt_name).trim()}${typLbl}`);
  } else if (item.snap_projekt_name) {
    parts.push(`Projekt: ${String(item.snap_projekt_name).trim()}`);
  }
  if (item.service_titel) parts.push(`Service: ${String(item.service_titel).trim()}`);
  if (item.snap_adresse) parts.push(clipText(item.snap_adresse, 64));
  if (item.abweichende_adresse) parts.push(clipText(item.abweichende_adresse, 64));
  const userLabel = item.user_name || (item.user_id ? users[item.user_id] : null);
  if (userLabel) parts.push(`Mitarbeiter: ${userLabel}`);
  if (item.typ && item.entity !== "kalender_eintraege") parts.push(String(item.typ).replace(/_/g, " "));
  if (item.kategorie) parts.push(item.kategorie);
  if (item.hersteller) parts.push(item.hersteller);
  if (item.seriennummer) parts.push(`SN: ${item.seriennummer}`);
  const datumOnly = formatDateOnly(item.datum);
  if (datumOnly) parts.push(`Datum: ${datumOnly}`);
  if (item.von_datum) {
    const von = formatDateOnly(item.von_datum);
    const bis = formatDateOnly(item.bis_datum);
    if (von && bis && bis !== von) parts.push(`${von} – ${bis}`);
    else if (von) parts.push(`ab ${von}`);
  }
  if (item.start_at) parts.push(formatDate(item.start_at));
  if (item.faellig_am) parts.push(`Fällig: ${formatDate(item.faellig_am)}`);
  if (item.grund) parts.push(clipText(item.grund, 48));
  if (item.notiz) parts.push(clipText(item.notiz, 56));
  if (item.kommentar) parts.push(clipText(item.kommentar, 56));
  if (item.originalname && item.originalname !== item.name) parts.push(item.originalname);
  if (item.betrag_gesamt != null) {
    parts.push(
      `CHF ${Number(item.betrag_gesamt).toLocaleString("de-CH", { minimumFractionDigits: 2 })}`,
    );
  }
  return parts.filter(Boolean);
}

function buildTags(item) {
  const tags = [];
  if (item.status) tags.push(item.status);
  if (item.falltyp && item.falltyp !== "NORMAL") tags.push(item.falltyp);
  if (item.typ) tags.push(item.typ);
  return tags;
}

export default function AdminPapierkorb() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [qInput, setQInput] = useState("");
  const [filters, setFilters] = useState({ entity: "", q: "" });
  const [users, setUsers] = useState({});
  const [actionLoading, setActionLoading] = useState(new Set());
  const debounceRef = useRef(null);

  useEffect(() => {
    document.title = "Papierkorb • WireTrack";
  }, []);

  useEffect(() => {
    api
      .get("/admin/users", { silent: true })
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(u => {
          map[u.id] = u.name || u.email;
        });
        setUsers(map);
      })
      .catch(() => {});
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (filters.entity) params.entity = filters.entity;
      if (filters.q) params.q = filters.q;
      const { data } = await api.get("/admin/trash", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchTrash();
  }, [fetchTrash]);

  const handleFilterChange = (key, value) => {
    setPage(0);
    setFilters(f => ({ ...f, [key]: value }));
  };

  const handleSearchChange = val => {
    setQInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleFilterChange("q", val), 300);
  };

  const entryLabel = (item) => formatEntryTitle(item, users);

  const handleRestore = async item => {
    const { ok } = await confirm({
      title: "Wiederherstellen",
      message: `"${entryLabel(item)}" wiederherstellen?`,
      confirmLabel: "Wiederherstellen",
      cancelLabel: "Abbrechen",
      variant: "success",
    });
    if (!ok) return;
    const key = `${item.entity}-${item.id}`;
    setActionLoading(prev => new Set(prev).add(key));
    try {
      await api.post(`/admin/trash/${item.entity}/${item.id}/restore`);
      showSuccess("Wiederhergestellt");
      fetchTrash();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "NOT_IN_TRASH") showError("Datensatz ist nicht im Papierkorb.");
      else showError(err);
    } finally {
      setActionLoading(prev => {
        const s = new Set(prev);
        s.delete(key);
        return s;
      });
    }
  };

  const handleHardDelete = async item => {
    const { ok } = await confirm({
      title: "Löschen",
      message: `"${entryLabel(item)}" löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      confirmLabel: "Löschen",
      cancelLabel: "Abbrechen",
      variant: "danger",
    });
    if (!ok) return;
    const key = `${item.entity}-${item.id}`;
    setActionLoading(prev => new Set(prev).add(key));
    try {
      await api.delete(`/admin/trash/${item.entity}/${item.id}`);
      showSuccess("Gelöscht");
      fetchTrash();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "NOT_IN_TRASH") showError("Nur Papierkorb-Einträge können endgültig gelöscht werden.");
      else showError(err);
    } finally {
      setActionLoading(prev => {
        const s = new Set(prev);
        s.delete(key);
        return s;
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = Boolean(filters.entity || filters.q);

  const renderRowActions = (item, busy) => (
    <div className={listRowActionsCls}>
      <AppButton
        type="button"
        variant="secondary"
        size="sm"
        className={entityRowCompactActionClassName}
        onClick={() => handleRestore(item)}
        disabled={busy}
        aria-label="Wiederherstellen"
      >
        Wiederherstellen
      </AppButton>
      <AppButton
        type="button"
        variant="secondary"
        size="sm"
        className={entityRowCompactDeleteActionClassName}
        onClick={() => handleHardDelete(item)}
        disabled={busy}
        aria-label="Löschen"
      >
        Löschen
      </AppButton>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white">
      <div className={cn("mx-auto max-w-6xl", mobilePagePaddingClassName)}>
        <div className="rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 mb-3 sm:mb-4 border transition-all app-frame">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-lg sm:text-2xl font-bold tracking-tight text-primary">
                Papierkorb
              </h1>
              <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm truncate text-secondary">
                {loading ? "lädt…" : `${total} ${total === 1 ? "Eintrag" : "Einträge"}`}
              </p>
            </div>
            <div
              className={cn(
                detailPageHeaderNavActionsClassName,
                "max-md:grid-cols-3 mt-0 sm:mt-0",
              )}
            >
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={cn(detailPageHeaderNavClassName, "max-md:w-full max-md:justify-center")}
                onClick={() => smartBack(nav, ["/admin", "/"])}
              >
                Zurück
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={cn(detailPageHeaderNavClassName, "max-md:w-full max-md:justify-center")}
                onClick={() => nav("/admin")}
              >
                Admin
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={cn(detailPageHeaderNavClassName, "max-md:w-full max-md:justify-center")}
                onClick={() => nav("/")}
              >
                <span className="md:hidden">Home</span>
                <span className="hidden md:inline">Dashboard</span>
              </AppButton>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2.5 md:gap-4">
          <div className="flex items-start gap-2.5 p-3 sm:p-4 rounded-xl border border-amber-500/25 bg-amber-500/10 text-amber-100/90">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs sm:text-sm leading-relaxed">
              Einträge werden nach <strong>30 Tagen</strong> automatisch endgültig gelöscht.
              Wiederhergestellte Einträge erscheinen wieder in den normalen Listen.
            </p>
          </div>

          {/* Liste */}
          <div className={`${cardCls} overflow-hidden`}>
            {/* Toolbar */}
            <div className="p-4 border-b border-default flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative min-w-0 flex-1 sm:max-w-md">
                <Search
                  className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
                  aria-hidden
                />
                <Input
                  type="search"
                  value={qInput}
                  placeholder="Suchen…"
                  aria-label="Papierkorb durchsuchen"
                  className="pl-9 w-full"
                  onChange={e => handleSearchChange(e.target.value)}
                  inputMode="search"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:justify-end">
                <OptionPopoverFilter
                  compact
                  value={filters.entity || "__all__"}
                  onChange={v => handleFilterChange("entity", v === "__all__" ? "" : v)}
                  options={ENTITY_FILTER_OPTIONS}
                  labelAll="Alle Entities"
                  title="Entity"
                  menuWidth={220}
                />
              </div>
            </div>

            {loading ? (
              <div className="p-10 text-center">
                <div
                  className="inline-block w-6 h-6 border-2 rounded-full animate-spin border-default border-t-sky-400/70"
                  role="status"
                  aria-label="Laden"
                ></div>
                <p className="mt-2 text-sm text-secondary">Laden…</p>
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center">
                <Trash2 className="w-10 h-10 mx-auto mb-3 text-muted opacity-40" aria-hidden />
                <p className="font-medium text-primary">Papierkorb ist leer</p>
                <p className="text-sm mt-1 text-secondary">
                  {hasFilters
                    ? "Keine Einträge für diese Filter."
                    : "Keine gelöschten Einträge vorhanden."}
                </p>
              </div>
            ) : (
              <>
                <div className="md:hidden divide-y divide-default">
                  {items.map(item => {
                    const age = daysAgo(item.deleted_at);
                    const daysLeft =
                      30 -
                      Math.floor((Date.now() - new Date(item.deleted_at).getTime()) / 86400000);
                    const urgent = daysLeft <= 7;
                    const itemKey = `${item.entity}-${item.id}`;
                    const busy = actionLoading.has(itemKey);
                    const tags = buildTags(item);
                    const meta = buildMetaLine(item);
                    const displayName = item.name || `#${item.id}`;
                    const delUser = item.deleted_by
                      ? users[item.deleted_by] || `#${item.deleted_by}`
                      : null;
                    return (
                      <article key={itemKey} className="p-3 sm:p-4 space-y-2 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <PillBadge label={item.entity_label} tone="info" size="sm" />
                          <span className="text-[10px] text-secondary tabular-nums shrink-0">#{item.id}</span>
                        </div>
                        <div className="font-semibold text-sm text-primary break-words line-clamp-2">{displayName}</div>
                        {tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {tags.map((t, i) => (
                              <PillBadge key={i} label={t} tone="neutral" size="sm" />
                            ))}
                          </div>
                        ) : null}
                        {meta.length > 0 ? (
                          <p className="text-[11px] text-secondary line-clamp-2">{meta.join(" · ")}</p>
                        ) : null}
                        <div className="text-[11px] text-secondary space-y-0.5">
                          <div>{formatDate(item.deleted_at)}</div>
                          {delUser ? <div>{delUser}</div> : null}
                          <div className={urgent ? "text-rose-300 font-medium" : ""}>
                            {age}
                            {" · "}
                            {daysLeft > 0 ? `${daysLeft}d übrig` : "abgelaufen"}
                          </div>
                        </div>
                        <div className="flex justify-end pt-0.5">{renderRowActions(item, busy)}</div>
                      </article>
                    );
                  })}
                </div>

                <div
                  className={`hidden md:block overflow-x-auto ${items.length > 10 ? "max-h-[min(70vh,640px)] overflow-y-auto" : ""}`}
                >
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-surface shadow-[0_1px_0_0_var(--border-default)]">
                      <tr>
                        <th className={cn(thCls, "w-[1%] whitespace-nowrap")}>Typ</th>
                        <th className={thCls}>Eintrag</th>
                        <th className={cn(thCls, "hidden md:table-cell")}>Gelöscht</th>
                        <th className={cn(thCls, "text-right w-[1%]")}>Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const age = daysAgo(item.deleted_at);
                        const daysLeft =
                          30 -
                          Math.floor((Date.now() - new Date(item.deleted_at).getTime()) / 86400000);
                        const urgent = daysLeft <= 7;
                        const itemKey = `${item.entity}-${item.id}`;
                        const busy = actionLoading.has(itemKey);
                        const tags = buildTags(item);
                        const meta = buildMetaLine(item);
                        const displayName = item.name || `#${item.id}`;
                        const delUser = item.deleted_by
                          ? users[item.deleted_by] || `#${item.deleted_by}`
                          : null;

                        return (
                          <tr
                            key={itemKey}
                            className={cn(
                              "border-t border-default hover:bg-muted/30 transition-colors",
                              idx % 2 === 1 && "bg-muted/10",
                            )}
                          >
                            <td className={tdCls}>
                              <PillBadge label={item.entity_label} tone="info" size="sm" />
                            </td>
                            <td className={tdCls}>
                              <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                                <span className="font-semibold truncate max-w-[280px]" title={displayName}>
                                  {displayName}
                                </span>
                                <span className="text-[11px] text-secondary tabular-nums">#{item.id}</span>
                                {tags.map((t, i) => (
                                  <PillBadge key={i} label={t} tone="neutral" size="sm" />
                                ))}
                              </div>
                              {meta.length > 0 ? (
                                <p className="mt-0.5 text-[11px] text-secondary leading-relaxed">
                                  {meta.join(" · ")}
                                </p>
                              ) : null}
                              <div className="md:hidden mt-1.5 text-[11px] text-secondary space-y-0.5">
                                <div>{formatDate(item.deleted_at)}</div>
                                {delUser ? <div>{delUser}</div> : null}
                                <div className={urgent ? "text-rose-300 font-medium" : ""}>
                                  {age}
                                  {" · "}
                                  {daysLeft > 0 ? `${daysLeft}d übrig` : "abgelaufen"}
                                </div>
                              </div>
                            </td>
                            <td className={cn(tdCls, "hidden md:table-cell text-sm")}>
                              <div className="text-primary">{formatDate(item.deleted_at)}</div>
                              <div className="mt-0.5 text-[11px] text-secondary">
                                {delUser ? <span>{delUser}</span> : null}
                                {delUser ? <span> · </span> : null}
                                <span className={urgent ? "text-rose-300 font-medium" : ""}>
                                  {age}
                                  {" · "}
                                  {daysLeft > 0 ? `${daysLeft}d übrig` : "abgelaufen"}
                                </span>
                              </div>
                              {item.delete_reason ? (
                                <p
                                  className="mt-0.5 text-[11px] text-secondary truncate max-w-[220px]"
                                  title={item.delete_reason}
                                >
                                  Grund: {item.delete_reason}
                                </p>
                              ) : null}
                            </td>
                            <td className={cn(tdCls, "text-right align-middle whitespace-nowrap")}>
                              {renderRowActions(item, busy)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-default">
                    <span className="text-sm text-secondary tabular-nums">
                      Seite {page + 1} von {totalPages} ({total} Einträge)
                    </span>
                    <div className="flex flex-wrap gap-2 sm:justify-end">
                      <AppButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                      >
                        Zurück
                      </AppButton>
                      <AppButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      >
                        Weiter
                      </AppButton>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2.5 border-t border-default text-xs text-secondary">
                    {total} {total === 1 ? "Eintrag" : "Einträge"}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="h-6 md:h-8" />
      </div>
    </div>
  );
}
